import { useCallback, useEffect, useRef, useState } from "react";

import { SEARCH_DEBOUNCE_MS } from "./constants";

export type AsyncListStatus = "idle" | "loading" | "success" | "error" | "forbidden";

export interface AsyncListPage<T> {
  items: T[];
  total: number;
}

export interface UseAsyncListOptions<P, T> {
  /** Busca uma página; use o `signal` na requisição (é abortado ao mudar `params` ou chamar `reload`). */
  fetcher: (params: P, signal: AbortSignal) => Promise<AsyncListPage<T>>;
  /** Parâmetros da busca; precisam ser serializáveis (a mudança é detectada por `JSON.stringify`). */
  params: P;
  /** `false` não busca e deixa o status em `idle`. Padrão: `true`. */
  enabled?: boolean;
  /** Espera antes de buscar quando `debounceKey` muda. Padrão: `SEARCH_DEBOUNCE_MS` (300). */
  debounceMs?: number;
  /** Só uma mudança desta chave busca com debounce (ex.: o termo digitado); as demais mudanças de `params` buscam na hora. */
  debounceKey?: unknown;
  /** Diz se o erro é falta de permissão. Padrão: `status === 403` em `err.response?.status ?? err.status`. */
  isForbidden?: (error: unknown) => boolean;
  forbiddenMessage: string;
  errorMessage: string;
  /** Mensagem própria do erro (ex.: a do servidor); vazia ou ausente, vale `errorMessage`. Não vale para o 403. */
  resolveErrorMessage?: (error: unknown) => string | null | undefined;
}

export interface UseAsyncListResult<T> {
  items: T[];
  total: number;
  status: AsyncListStatus;
  /** Mensagem do erro ou da falta de permissão; `null` fora desses estados. */
  errorMessage: string | null;
  /** Refaz a busca na hora, sem debounce. */
  reload: () => void;
  isLoading: boolean;
}

interface ListState<T> {
  status: AsyncListStatus;
  items: T[];
  total: number;
  errorMessage: string | null;
}

function isForbiddenByStatus(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const { response, status } = error as { response?: { status?: unknown }; status?: unknown };
  return (response?.status ?? status) === 403;
}

/**
 * Lista assíncrona com ciclo loading/success/error/forbidden, abort da requisição anterior,
 * debounce só para o termo de busca e `reload`. Mantém os `items` anteriores durante o `loading`.
 */
export function useAsyncList<P, T>({
  fetcher,
  params,
  enabled = true,
  debounceMs = SEARCH_DEBOUNCE_MS,
  debounceKey,
  isForbidden = isForbiddenByStatus,
  forbiddenMessage,
  errorMessage,
  resolveErrorMessage,
}: UseAsyncListOptions<P, T>): UseAsyncListResult<T> {
  const [state, setState] = useState<ListState<T>>({
    status: enabled ? "loading" : "idle",
    items: [],
    total: 0,
    errorMessage: null,
  });
  const [reloadToken, setReloadToken] = useState(0);

  const latest = useRef({
    fetcher,
    params,
    isForbidden,
    forbiddenMessage,
    errorMessage,
    resolveErrorMessage,
  });
  latest.current = {
    fetcher,
    params,
    isForbidden,
    forbiddenMessage,
    errorMessage,
    resolveErrorMessage,
  };

  const previousKey = useRef<{ value: unknown } | null>(null);
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    const previous = previousKey.current;
    const shouldDebounce = previous !== null && !Object.is(previous.value, debounceKey);
    previousKey.current = { value: debounceKey };

    if (!enabled) {
      setState((current) => ({ ...current, status: "idle", errorMessage: null }));
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    setState((current) => ({ ...current, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        const { fetcher: fetch, params: currentParams } = latest.current;
        // `Promise.resolve().then`: um fetcher não-async que lança vira rejeição, não erro solto no timer.
        Promise.resolve()
          .then(() => fetch(currentParams, controller.signal))
          .then(({ items, total }) => {
            if (cancelled) return;
            setState({ status: "success", items, total, errorMessage: null });
          })
          .catch((error: unknown) => {
            if (cancelled) return;
            const {
              isForbidden: forbidden,
              forbiddenMessage: denied,
              errorMessage: failed,
              resolveErrorMessage: resolve,
            } = latest.current;
            let isDenied = false;
            try {
              isDenied = forbidden(error);
            } catch {
              // `isForbidden` que lança conta como erro comum.
            }
            setState({
              status: isDenied ? "forbidden" : "error",
              items: [],
              total: 0,
              errorMessage: isDenied ? denied : resolve?.(error) || failed,
            });
          });
      },
      shouldDebounce ? debounceMs : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
    // `debounceKey` só decide o atraso desta execução; mudar só ele já reexecuta o efeito de propósito.
  }, [paramsKey, debounceKey, debounceMs, enabled, reloadToken]);

  const reload = useCallback(() => {
    previousKey.current = null;
    setReloadToken((token) => token + 1);
  }, []);

  return { ...state, reload, isLoading: state.status === "loading" };
}
